import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject, timer } from 'rxjs';
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators';

import {
  AbiProfileRequestModel,
  CompanyControllingPersonModel,
  CompanyExternalIds,
  CompanyModel,
  SelfFilerCodeRequestModel,
} from '@data-layer/models/companies';
import { UserBetaFlagModel, UserDeletionBlockersResultModel, UserModel } from '@data-layer/models/user';
import { enumUserBetaFlag, enumUserRole } from '@data-layer/enums/user';
import { EntityModel } from '@data-layer/models/entities';
import { BUSINESS_TYPES, minControllingPersons } from '@constants/business-types';
import { IsfV1EntityModel } from '@data-layer/models/isf-v1';
import {
  BetaFlagsRepository,
  BillsRepository,
  CompaniesRepository,
  LoginAttemptsRepository,
  SelfFilerRepository,
  UsersRepository,
} from '@data-layer/repositories';
import { LoginAttemptModel, PaginatedResultModel } from '@data-layer/models/common';
import { ApiErrorService } from './api-error.service';
import { FetchAllHints, FetchAppBetaFlags } from '@app/shared/shared-state';
import { Store } from '@ngxs/store';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { createControllingPersonFormGroup } from '@helpers/controlling-person-form.helper';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import {
  bondsFormFields,
  bondsSideBarValidationKeys,
  bondsValidationKeys,
  cbpAssignedNumberRequestFormFields,
  esFormFields,
  esSideBarValidationKeys,
  esValidationKeys,
  fullCompanyFormFields,
  fullCompanyFormKeys,
  fullCompanyValidationKeys,
  isfFormFields,
  isfSideBarValidationKeys,
  isfValidationKeys,
  lclFormFields,
  lclNonAllowCustomsValidationKeys,
  lclSideBarValidationKeys,
  lclValidationKeys,
  nonUsCompanyChangesKeys,
  usCompanyChangesKeys,
} from '@constants/company-validation-keys';
import { enumCbpAssignedNumberStatus, enumIdentifierQualifier } from '@data-layer/enums/common';
import { cloneDeep, isEqual } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { GenericService } from '@services/generic.service';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BillModel, BillSearchModel } from '@data-layer/models/payments';
import {
  CANADA_COUNTRY_CODE,
  DEFAULT_MAX_POLLING_RETRY,
  DEFAULT_POLLING_TIMEOUT,
  MEXICO_COUNTRY_CODE,
  USA_COUNTRY_CODE,
} from '@constants/constants';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { BetaFeatureSearchModel } from '@app/data-layer/models/common/beat-feature-search-model';

@Injectable({
  providedIn: 'root',
})
export class CompaniesService implements CanActivate {
  public companyChange$: BehaviorSubject<{ previous: CompanyModel; current: CompanyModel }>;
  public companiesChange$: BehaviorSubject<{ previous: CompanyModel[]; current: CompanyModel[] }>;
  public userChange$: BehaviorSubject<{ previous: UserModel; current: UserModel }>;
  public userBetaFlagsChange$: BehaviorSubject<UserBetaFlagModel[]> = new BehaviorSubject<UserBetaFlagModel[]>([]);
  public companyUpdated$: Subject<void> = new Subject<void>();
  public companyFailedBill$: ReplaySubject<BillModel> = new ReplaySubject<BillModel>();

  private _user: UserModel;
  private _companies: CompanyModel[] = [];
  private _placement: enumCompanyPlacement;
  private _companyExternalIds: CompanyExternalIds;

  private get company(): CompanyModel {
    const defaultCompanyId: number = this.user?.defaultCompanyId;
    return this.getCompanyById(defaultCompanyId) || this.companies[0];
  }

  public getCompanyById(companyId: number): CompanyModel {
    return this.companies.find((company: CompanyModel) => company.id === companyId);
  }

  public getCompanyByIdFromServer(companyId: number): Observable<CompanyModel> {
    return this.companiesRepository.getCompanyById(companyId);
  }

  public getCompanyAddressLine(company: CompanyModel): string {
    return [
      [company?.address?.line1, company?.address?.line2, company?.address?.line3].join(' '),
      company?.address?.city,
      [company?.address?.state?.code, company?.address?.postalCode].join(' '),
      company?.address?.country?.description,
    ]
      .filter((item: string) => !!item?.trim())
      .join(', ');
  }

  public get companyExternalIds(): CompanyExternalIds {
    return this._companyExternalIds;
  }

  private set companyExternalIds(data: CompanyExternalIds) {
    this._companyExternalIds = data;
  }

  public get userExternalId(): string {
    return this.companyExternalIds?.externalUserId || this.user?.externalId;
  }

  public get userLegalTermsAcceptedDate(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.legal_terms_accepted_date || null;
  }

  public get userPrivacyPolicyAcceptedDate(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.privacy_policy_accepted_date || null;
  }

  private set company(company: CompanyModel) {
    const previous: CompanyModel = cloneDeep(this.company);
    if (company && company.id && company.id !== previous?.id) {
      this.usersRepository
        .setUserCurrentCompany(company.id)
        .pipe(
          switchMap(() => this.getUserData(true)),
          take(1),
        )
        .subscribe(() => {
          this.companyChange$.next({ previous, current: this.company });
        });
    }

    // TODO check it
    // this.router.navigate(['/dashboard']);
  }

  private get companies(): CompanyModel[] {
    return this._companies;
  }

  public set placement(placement: enumCompanyPlacement) {
    this._placement = placement;
  }

  public get placement(): enumCompanyPlacement {
    return this._placement;
  }

  private set companies(companies: CompanyModel[]) {
    const previous: CompanyModel[] = cloneDeep(this._companies);
    const previousCompany: CompanyModel = cloneDeep(this.company);
    this._companies = companies || [];
    this.companiesChange$.next({
      previous,
      current: this._companies,
    });
    if (!isEqual(previousCompany, this.company)) {
      this.companyChange$.next({ previous: previousCompany, current: this.company });
    }
  }

  private get user(): UserModel {
    return this._user;
  }

  private set user(user: UserModel) {
    const previous: UserModel = cloneDeep(this._user);
    this._user = cloneDeep(user);
    this.userChange$.next({
      previous,
      current: this.user,
    });
    if (!isEqual(previous, this.user)) {
      this.userChange$.next({ previous, current: this.user });
    }

    this.apiErrorService.setUser(this.user);
  }

  constructor(
    private router: Router,
    private organizationRepository: OrganizationsRepository,
    private companiesRepository: CompaniesRepository,
    private selfFilerRepository: SelfFilerRepository,
    private usersRepository: UsersRepository,
    private loginAttemptsRepository: LoginAttemptsRepository,
    private apiErrorService: ApiErrorService,
    private store: Store,
    private fb: UntypedFormBuilder,
    private betaFlagsRepository: BetaFlagsRepository,
    private genericService: GenericService,
    private jwtHelperService: JwtHelperService,
    private billsRepository: BillsRepository,
  ) {
    this.companyChange$ = new BehaviorSubject({ previous: null, current: null });
    this.companiesChange$ = new BehaviorSubject({ previous: null, current: [] });
    this.userChange$ = new BehaviorSubject({ previous: null, current: null });

    //  TODO: Discuss about redirect behavior after changing company through all application
    /*this.companyChange$.pipe(takeUntil(this.destroy$)).subscribe((companyInstance: { previous: CompanyModel; current: CompanyModel }) => {
      if (companyInstance.current?.id !== companyInstance?.current?.id) {
        this.router.navigate(['/dashboard']);
      }
    });*/
  }

  public createCompanyForm(placement: enumCompanyPlacement, _company?: CompanyModel): UntypedFormGroup {
    this.placement = placement;
    const company: CompanyModel = !!_company ? _company : this.company;
    const companyForm: UntypedFormGroup = this.fb.group({});
    this.formFields(placement).forEach((field: { key: string; validators: ValidatorFn[]; children?: any }) => {
      if (field.children) {
        const childFormGroup: UntypedFormGroup = this.fb.group({});
        field.children.forEach((child: { key: string; validators: ValidatorFn[]; children?: any }) => {
          childFormGroup.addControl(
            child.key,
            this.fb.control(!!company[field.key] ? company[field.key][child.key] : null, child.validators),
          );
        });
        companyForm.addControl(field.key, childFormGroup);

        if (
          field.key === 'address' &&
          this.isHasAssignedCbpRequest &&
          (this.isAssignedCbpRequestSubmittedOrApproved(company) || this.isAssignedCbpRequestRejected(company))
        ) {
          companyForm.get('address').disable();
        }
      } else {
        companyForm.addControl(field.key, this.fb.control(company[field.key], field.validators));

        if (placement === enumCompanyPlacement.CBP_ASSIGNED_NUMBER_REQUEST && field.key === 'phone') {
          companyForm.get('phone').patchValue(null);
        }

        if (
          this.isHasAssignedCbpRequest &&
          (this.isAssignedCbpRequestSubmittedOrApproved(company) || this.isAssignedCbpRequestRejected(company))
        ) {
          switch (field.key) {
            case 'businessName':
            case 'businessType':
            case 'identifier':
              companyForm.get(field.key).disable();
              break;
            case 'identifierQualifier':
              companyForm.get('identifierQualifier').patchValue(enumIdentifierQualifier.ANI);
              break;
            default:
              break;
          }
        } else {
          if (field.key === 'identifierQualifier' && !companyForm.get('identifierQualifier')?.value) {
            companyForm.get('identifierQualifier').patchValue(companyForm.get('isUnitedStates').value ? enumIdentifierQualifier.EIN : null);
          }
        }
      }
    });

    // if (!companyForm.controls.isUnitedStates.value) {
    companyForm.addControl('valueAddedTaxNumber', this.fb.control(company.valueAddedTaxNumber));
    companyForm.addControl('isAllowedForCustoms', this.fb.control(company.isAllowedForCustoms));
    // }

    if (enumCompanyPlacement.ENTRY_SUMMARY === placement || enumCompanyPlacement.COMPANY_PAGE === placement) {
      companyForm.addControl('achIsProvided', this.fb.control(!!company.payerUnitNumber && company.authorizationAgreementAccepted));
    }

    // #13269 — controlling persons exist ONLY on the company page; the other placements must not
    // gain the controls (their absence is the isolation mechanism, and the PUT payload is protected
    // by the stored-model echo in updateCompany).
    if (enumCompanyPlacement.COMPANY_PAGE === placement) {
      companyForm.addControl('partnershipType', this.fb.control(company.partnershipType));
      // #13282 — the per-type minimum is a FE rule: the backend legitimately stores fewer people
      // (a company saved "as is" has none), so the missing sections are rendered as empty ones.
      const storedPersons: CompanyControllingPersonModel[] = company.controllingPersons || [];
      const personsCount: number = Math.max(storedPersons.length, minControllingPersons(company.businessType));
      companyForm.addControl(
        'controllingPersons',
        new UntypedFormArray(
          Array.from({ length: personsCount }, (_: unknown, index: number) => createControllingPersonFormGroup(storedPersons[index])),
        ),
      );
    }
    return companyForm;
  }

  public validateCompany(
    placement: enumCompanyPlacement,
    validateByFormFields: boolean = false,
    _company?: CompanyModel,
    isChangesAfterValidationDate?: boolean,
  ): boolean {
    const companyValidation: any = {};
    const company: CompanyModel = !!_company ? _company : this.company;
    let isValid: boolean = true;
    (validateByFormFields
      ? this.sideBarValidationKeys(placement, company.isUnitedStates, company.isAllowedForCustoms, isChangesAfterValidationDate)
      : this.validationKeys(placement, company.isAllowedForCustoms, company.isUnitedStates)
    ).forEach((field: { key: string; validators: ValidatorFn[]; children?: any }) => {
      if (field.children) {
        field.children.forEach((child: { key: string; validators: ValidatorFn[]; children?: any }) => {
          companyValidation[child.key] = UtilsHelper.isNull(company[field.key])
            ? false
            : !UtilsHelper.isNullOrUndefined(company[field.key][child.key]) && !(company[field.key][child.key] === '');

          const withStateCountryCodes = [CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE];

          const countryCode = company?.address?.country?.code;

          if (
            (!!countryCode && child.key === 'state' && !withStateCountryCodes.includes(countryCode)) ||
            (child.key === 'state' && !company.isUnitedStates) ||
            (child.key === 'state' &&
              this.isHasAssignedCbpRequest &&
              (this.isAssignedCbpRequestSubmittedOrApproved(company) || this.isAssignedCbpRequestRejected(company)))
          ) {
            companyValidation[child.key] = true;
          }

          if (!companyValidation[child.key] && !child.validators?.length) {
            companyValidation[child.key] = true;
          }
        });
      } else {
        companyValidation[field.key] = !UtilsHelper.isNullOrUndefined(company[field.key]) && !(company[field.key] === '');
        // TODO: validate isAllowedRegistration
        if (field.key === 'isAllowedRegistration' && enumCompanyPlacement.SHIPMENT !== placement) {
          companyValidation[field.key] = company[field.key];
        }
        // TODO: skip validation to identifierQualifier if non-usa, look #8012 fix to make identifierQualifier empty
        if (
          (field.key === 'identifierQualifier' && !company.isUnitedStates) ||
          (field.key === 'phone' && !!this.user.phoneNumber) ||
          (field.key === 'authorizationAgreementAccepted' && !this.company.payerUnitNumber)
        ) {
          companyValidation[field.key] = true;
        }
        if (field.key === 'phone' && !this.user?.phoneNumber) {
          companyValidation[field.key] = false;
        }
      }
    });
    for (const key in companyValidation) {
      if (companyValidation.hasOwnProperty(key)) {
        if (companyValidation[key] === false) {
          isValid = false;
          break;
        }
      }
    }

    return isValid;
  }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    if (!!this.company) {
      return of(true);
    }

    return this.getCompaniesFromServer().pipe(
      tap((res: CompanyModel[]) => {
        this.companies = res;
      }),
      map(() => {
        if (!this.company) {
          this.router.navigate(['/companies', 'account-settings', 'create']);
          return false;
        }

        return true;
      }),
    );
  }

  public getCompaniesFromServer(companyId?: number, isShowLoader?: boolean): Observable<CompanyModel[]> {
    const defaultOrganizationId = this.user.defaultOrganizationId;

    return defaultOrganizationId
      ? this.getOrganizationCompanies(defaultOrganizationId, companyId, isShowLoader)
      : this.organizationRepository.getOrganizations().pipe(
          switchMap((organizations) => this.getOrganizationCompanies(organizations[0].id, companyId, isShowLoader)),
          take(1),
        );
  }

  public getOrganizationCompanies(organizationId: number, companyId?: number, isShowLoader?: boolean): Observable<CompanyModel[]> {
    let organizationCompanies: CompanyModel[] = [];

    return this.organizationRepository.getOrganizationCompanies(organizationId, isShowLoader).pipe(
      switchMap((res: CompanyModel[]) => {
        const currentCompanyId = companyId || this.company?.id || this.user?.defaultCompanyId || res[0]?.id;
        organizationCompanies = res;
        return this.getCompanyByIdFromServer(currentCompanyId);
      }),
      switchMap((company: CompanyModel) => {
        const currentCompanyIndex = organizationCompanies.length === 1 ? 0 : organizationCompanies.findIndex((c) => c.id === company.id);
        if (currentCompanyIndex !== -1) {
          const companies = organizationCompanies;
          companies[currentCompanyIndex] = company;
          this.companies = companies;
        } else {
          this.companies = organizationCompanies;
        }
        return of(this.companies);
      }),
    );
  }

  public getUserData(forceUpdate: boolean = false, isShowLoader: boolean = false): Observable<UserModel> {
    if (this.user && !forceUpdate) {
      return of(this.user);
    }

    const { roles, uid, betaFlags } = this.user ? this.user : { roles: [], uid: null, betaFlags: [] };
    return this.usersRepository.get(isShowLoader).pipe(
      tap((res: UserModel) => {
        this.user = res ? { ...res, roles, uid, betaFlags } : null;
      }),
    );
  }

  public getUserBetaFlags(params: BetaFeatureSearchModel, isShowLoader: boolean = false): Observable<UserBetaFlagModel[]> {
    return this.betaFlagsRepository.getAppBetaFeatures(params, isShowLoader).pipe(
      map((res: UserBetaFlagModel[]) => {
        const isfValue: UserBetaFlagModel = {
          id: 0,
          displayName: enumUserBetaFlag.isfDesktop,
          tag: enumUserBetaFlag.isfDesktop,
        };
        const esBrokerValue: UserBetaFlagModel = {
          id: 0,
          displayName: enumUserBetaFlag.esBrokerDesktop,
          tag: enumUserBetaFlag.esBrokerDesktop,
        };

        // Enable ISF & ES-Broker desktop for all
        return [...res, isfValue, esBrokerValue];
      }),
    );
  }

  public getHints(): void {
    this.store.dispatch(new FetchAllHints());
  }

  public getCompany(): CompanyModel {
    return cloneDeep(this.company);
  }

  public getCompanies(): CompanyModel[] {
    return cloneDeep(this.companies);
  }

  public setDefaultCompany(company: CompanyModel, callback?: () => void): void {
    const orgId = this.user?.defaultOrganizationId;
    if (orgId && company?.id) {
      this.saveOrgCompanyPreference(orgId, company.id);
    }
    this.company = company;
    if (callback) {
      callback();
    }
  }

  public syncAfterCompanyDeleted(deletedCompanyId: number): Observable<CompanyModel[]> {
    const currentDefault: CompanyModel = this.getCompany();
    const remaining: CompanyModel[] = this.getCompanies().filter((c: CompanyModel) => c.id !== deletedCompanyId);
    const nextCompany: CompanyModel = remaining[0];

    if (!nextCompany) {
      this.clearStateCompany();
      return of([]);
    }

    const wasDefaultDeleted: boolean = currentDefault?.id === deletedCompanyId;
    if (wasDefaultDeleted) {
      this.setDefaultCompany(nextCompany);
    }
    const targetId: number = wasDefaultDeleted ? nextCompany.id : currentDefault.id;
    return this.getCompaniesFromServer(targetId);
  }

  public getOrgCompanyPreference(organizationId: number): number | null {
    return this.getOrgCompanyPreferences()[organizationId] || null;
  }

  public setUser(user: UserModel): void {
    const { roles, uid } = this.user;
    this.user = { ...user, roles, uid };
  }

  public getUser(): UserModel {
    return this.user;
  }

  public getUserLastLoginAttempt(isShowLoader: boolean): Observable<LoginAttemptModel> {
    return this.loginAttemptsRepository
      .getUserLoginAttempts(0, 2, isShowLoader)
      .pipe(
        map((attempts: PaginatedResultModel<LoginAttemptModel>) =>
          attempts.items.length === 2 ? UtilsHelper.lastElement<LoginAttemptModel>(attempts.items) : attempts.items[0],
        ),
      );
  }

  public getUserLoginAttempts(offset: number = 0, limit: number = null): Observable<PaginatedResultModel<LoginAttemptModel>> {
    return this.loginAttemptsRepository.getUserLoginAttempts(offset, limit);
  }

  public getRoles(): enumUserRole[] {
    return this.user && this.user.roles ? this.user.roles : [];
  }

  public getAllUserBetaFlags(): UserBetaFlagModel[] {
    return this.user && this.user.betaFlags ? this.user.betaFlags : [];
  }

  /** True when the loaded user beta flags (fetched per user + company/org at login and company switch) contain the tag. */
  public hasUserBetaFlag(tag: enumUserBetaFlag): boolean {
    return this.getAllUserBetaFlags().some((flag: UserBetaFlagModel) => flag.tag === tag);
  }

  /**
   * #13261/#13414: Truck transportation mode is a beta feature —
   * one gate for the create page, the details sidebar and the mobile modal.
   */
  public get isEsTruckModeAvailable(): boolean {
    return this.hasUserBetaFlag(enumUserBetaFlag.esTruckerIntegration);
  }

  public setUserRoles(roles: enumUserRole[] | enumUserRole): void {
    if (typeof roles === 'string') {
      this.user.roles = [roles as enumUserRole];
    } else {
      this.user.roles = roles;
    }
  }

  public setUserBetaFlags(flags: UserBetaFlagModel[]): void {
    this.user.betaFlags = flags;
    this.userBetaFlagsChange$.next(flags);
  }

  public setUserUID(uid: string): void {
    this.user.uid = uid;
  }

  public isSupport(): boolean {
    return this.getRoles().indexOf(enumUserRole.SUPPORT) !== -1;
  }

  public isAdmin(): boolean {
    return this.getRoles().indexOf(enumUserRole.ADMIN) !== -1;
  }

  public isUser(): boolean {
    return this.getRoles().indexOf(enumUserRole.USER) !== -1;
  }

  public isSuperAdmin(): boolean {
    return this.getRoles().indexOf(enumUserRole.SUPER_ADMIN) !== -1;
  }

  public isAdminOrSuper(): boolean {
    return this.isAdmin() || this.isSuperAdmin();
  }

  public isSupportOrAdmin(): boolean {
    return this.isSupport() || this.isAdminOrSuper();
  }

  public isCustomsBroker(): boolean {
    return this.getRoles().indexOf(enumUserRole.CUSTOMS_BROKER) !== -1;
  }

  public isCustomsBrokerOnly(): boolean {
    const roles = this.getRoles();
    return (
      roles.includes(enumUserRole.CUSTOMS_BROKER) &&
      !roles.includes(enumUserRole.SUPPORT) &&
      !roles.includes(enumUserRole.ADMIN) &&
      !roles.includes(enumUserRole.SUPER_ADMIN) &&
      !roles.includes(enumUserRole.USER)
    );
  }

  public isAdminOrSuperAdminByRoles(roles: enumUserRole[]): boolean {
    if (!roles) {
      return;
    }

    return roles.includes(enumUserRole.ADMIN) || roles.includes(enumUserRole.SUPER_ADMIN);
  }

  public getCompanyAsync(forceUpdate: boolean = false, isShowLoader: boolean = false, companyId?: number): Observable<CompanyModel> {
    if (!!this.company && !forceUpdate) {
      return of(cloneDeep(this.company));
    }

    return this.getCompaniesFromServer(companyId, isShowLoader).pipe(
      tap((res: CompanyModel[]) => {
        this.companies = res;
      }),
      map(() => (!!companyId ? this.getCompanyById(companyId) : this.company)),
    );
  }

  public createCompanyFromEntityModel(entity: EntityModel | IsfV1EntityModel, phoneNumber: string = null): Observable<CompanyModel> {
    const company: CompanyModel = this.getCompany();
    if (!company && entity) {
      const { address, dateOfBirth, name } = entity;
      const identifier: string = (entity as IsfV1EntityModel).identifier || (entity as EntityModel).entityIdentifier;
      const identifierQualifier: enumIdentifierQualifier =
        (entity as IsfV1EntityModel).identifierQualifier || (entity as EntityModel).entityIdentifierQualifier;
      const { firstName, lastName } = this.getUser();
      const newCompany: CompanyModel = {
        address: { ...address },
        dateOfBirth: dateOfBirth as any,
        businessName: name,
        identifier,
        identifierQualifier,
        firstName,
        lastName,
        businessType: BUSINESS_TYPES[0].value,
      } as CompanyModel;
      if (phoneNumber) {
        return this.updateUser({ ...this.user, phoneNumber }).pipe(switchMap(() => this.updateCompany(newCompany)));
      }
      return this.updateCompany(newCompany);
    }
    return of(null);
  }

  public updateCompany(
    _company: CompanyModel,
    updateAuthorizationAgreement: boolean = false,
    organizationId?: number,
  ): Observable<CompanyModel> {
    if (!_company.id) {
      let createCompany$: Observable<CompanyModel> = this.organizationRepository.createOrganizationCompany(organizationId, _company);

      return createCompany$.pipe(
        switchMap((createdCompany: CompanyModel) =>
          updateAuthorizationAgreement
            ? this.companiesRepository.acceptAuthorizationAgreement(createdCompany.id, true).pipe(map(() => createdCompany))
            : of(createdCompany),
        ),
        // #13269 — the create POST (CompanyCreateModel) does not accept controllingPersons;
        // persist the people entered on the create page with a follow-up PUT. The PUT is a full
        // replace, so its body must be the server's post-create model (which carries organizationId,
        // payment types etc. the create form never collects) with ONLY the people patched in —
        // never a spread of the create-form payload.
        switchMap((createdCompany: CompanyModel) => {
          if (!_company.controllingPersons || _company.controllingPersons.length === 0) {
            return of(createdCompany);
          }
          return this.companiesRepository
            .update(createdCompany.id, { ...createdCompany, controllingPersons: _company.controllingPersons })
            .pipe(map(() => createdCompany));
        }),
        switchMap((createdCompany: CompanyModel) => {
          this.setDefaultCompany(createdCompany);
          return this.getCompanyAsync(true, false, createdCompany.id);
        }),
      );
    }
    return this.withStoredControllingPersons(_company).pipe(
      switchMap((company: CompanyModel) => {
        if (updateAuthorizationAgreement) {
          return this.companiesRepository.acceptAuthorizationAgreement(company.id, true).pipe(
            switchMap(() => this.companiesRepository.update(company.id, company)),
            switchMap(() => this.getCompanyAsync(true, false, company.id)),
          );
        }
        return this.companiesRepository.update(company.id, company).pipe(switchMap(() => this.getCompanyAsync(true, false, company.id)));
      }),
    );
  }

  public acceptAuthorizationAgreement(): Observable<CompanyModel> {
    return this.companiesRepository.acceptAuthorizationAgreement(this.company.id, true).pipe(switchMap(() => this.getCompanyAsync(true)));
  }

  public updateCompanyValidationState(validationArea: enumCompanyPlacement): Observable<CompanyModel> {
    return this.companiesRepository.updateCompanyValidationState(this.company.id, validationArea);
  }

  public updateUser(user: UserModel): Observable<never> {
    return this.usersRepository.update(user).pipe(
      tap(() => {
        this.user = { ...this.user, ...(user || null) };
      }),
    );
  }

  public getDeletionBlockers(): Observable<UserDeletionBlockersResultModel> {
    return this.usersRepository.getDeletionBlockers();
  }

  public deleteUser(reason: string): Observable<never> {
    return this.usersRepository.delete(reason);
  }

  public clearStateCompany(): void {
    this.company = null;
    this.companies = [];
  }

  public clearStateUser(): void {
    this.user = null;
  }

  public sendSelfFilerRequest(body: SelfFilerCodeRequestModel, companyId?: number): Observable<undefined> {
    if (!companyId) {
      companyId = this.company.id;
    }
    return this.selfFilerRepository.sendFilerCodeRequest(companyId, body).pipe(
      switchMap(() => this.getCompanyAsync(true)),
      map(() => undefined),
    );
  }

  public sendAbiProfileRequest(body: AbiProfileRequestModel, companyId?: number): Observable<undefined> {
    if (!companyId) {
      companyId = this.company.id;
    }
    return this.selfFilerRepository.sendAbiProfileRequest(companyId, body).pipe(
      switchMap(() => this.getCompanyAsync(true)),
      map(() => undefined),
    );
  }

  public phoneConfirmation(phoneNumber: string): Observable<{ accepted: boolean }> {
    return this.usersRepository.phoneConfirmation(phoneNumber);
  }

  public confirmationPhoneCode(phoneNumber: string, code: string): Observable<{ valid: boolean }> {
    return this.usersRepository.confirmationPhoneCode(phoneNumber, code);
  }

  public isCouponsAvailable(companyId: number, productCodes: string[]): Observable<boolean> {
    return this.companiesRepository.isCouponsAvailable(companyId, productCodes);
  }

  public get isHasActiveACH(): boolean {
    const company: CompanyModel = this.getCompany();

    return !!company?.authorizationAgreementAccepted && !!company?.payerUnitNumber;
  }

  public getCompanyExternalIds(companyId: number): Observable<CompanyExternalIds> {
    return this.companiesRepository.getCompanyExternalIds(companyId).pipe(
      tap((data: CompanyExternalIds) => {
        this.companyExternalIds = data;
      }),
    );
  }

  public deleteDefaultPaymentMethod(companyId: number, isShowLoader: boolean): Observable<CompanyModel> {
    return this.companiesRepository.deleteDefaultPaymentMethod(companyId, isShowLoader);
  }

  public getBrokerPoaFileUrl(companyId: number): Observable<string> {
    return this.companiesRepository.getBrokerPoaFile(companyId).pipe(map((res: { poaTemplateUrl: string }) => res.poaTemplateUrl));
  }

  // is used for #8028
  public prepareHotjarData(): void {
    const hjObj: Function = (window as any)?.hj;

    if (!!hjObj) {
      hjObj('identify', this.userExternalId, {
        externalUserId: this.userExternalId,
        externalCompanyId: this.companyExternalIds?.externalCompanyId,
        clientVersion: this.genericService.appVersion$?.getValue()?.version,
        timestamp: new Date(),
      });
    }
  }

  public companyPayBill(
    billId: number,
    type?: enumCompanyPaymentType,
    params?: BillSearchModel,
  ): Observable<PaginatedResultModel<BillModel>> {
    return this.billsRepository.createPaymentIntent(billId, true, true).pipe(
      switchMap(() => {
        return timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.billsRepository.search(this.company.id, { ...(!!type && { type }), ...(!!params && params) }, true)),
          filter(
            (result: PaginatedResultModel<BillModel>) =>
              (result?.items || []).find((item: BillModel) => item.id === billId)?.state !== enumPaymentState.DELAYED,
          ),
          first(),
        );
      }),
      switchMap(() => this.billsRepository.search(this.company.id, { ...(!!type && { type }), ...(!!params && params) }, true)),
    );
  }

  public getCompanyMonthlyBills(): Observable<PaginatedResultModel<BillModel>> {
    return this.billsRepository.search(this.company.id, { type: enumCompanyPaymentType.MONTHLY }, true);
  }

  private isAssignedCbpRequestSubmittedOrApproved(company: CompanyModel): boolean {
    return this.isAssignedCbpRequestSubmitted(company) || this.isAssignedCbpRequestApproved(company);
  }

  private isAssignedCbpRequestSubmitted(company: CompanyModel): boolean {
    return (
      company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
      company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING
    );
  }

  private isAssignedCbpRequestApproved(company: CompanyModel): boolean {
    return company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED;
  }

  private isAssignedCbpRequestRejected(company: CompanyModel): boolean {
    return company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.REJECTED;
  }

  private get isHasAssignedCbpRequest(): boolean {
    return this.getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }

  // #13269 — PUT companies/{id} is a full replace: a payload without controllingPersons (or with null)
  // CLEARS the stored people, and a missing partnershipType clears the sub-type. Only the company-page
  // form carries these controls, so every other caller (ES/ISF/LCL/bonds/CBP sidebars, poa-purchase,
  // shipment-data, register) must echo the stored values back. The in-memory cache may hold a
  // ShortCompanyModel (organization companies list) that has no such fields at all, so the echo only
  // trusts a model proven full (a full GET returns controllingPersons as [], never null) and otherwise
  // fetches the company from the server first.
  private withStoredControllingPersons(company: CompanyModel): Observable<CompanyModel> {
    if (company.controllingPersons != null) {
      return of(company);
    }
    const cached: CompanyModel = this.getCompanyById(company.id);
    const stored$: Observable<CompanyModel> =
      cached && cached.controllingPersons != null ? of(cached) : this.companiesRepository.getCompanyById(company.id, false);
    return stored$.pipe(
      map((stored: CompanyModel) => ({
        ...company,
        controllingPersons: stored.controllingPersons,
        partnershipType: company.partnershipType == null ? stored.partnershipType : company.partnershipType,
      })),
    );
  }

  private validationKeys(
    placement: enumCompanyPlacement,
    isAllowedForCustoms: boolean,
    isUnitedStates: boolean,
  ): Array<{ key: string; validators: Validators[] }> {
    switch (placement) {
      case enumCompanyPlacement.ENTRY_SUMMARY:
        return esValidationKeys;
      case enumCompanyPlacement.ISF:
        return isfValidationKeys;
      case enumCompanyPlacement.SHIPMENT:
        return (isAllowedForCustoms && !isUnitedStates) || isUnitedStates ? lclValidationKeys : lclNonAllowCustomsValidationKeys;
      case enumCompanyPlacement.COMPANY_PAGE:
        return fullCompanyValidationKeys;
      case enumCompanyPlacement.CONTINUOUS_BOND:
        return bondsValidationKeys;
    }
  }

  private formFields(placement: enumCompanyPlacement): Array<{ key: string; validators: Validators[] }> {
    switch (placement) {
      case enumCompanyPlacement.ENTRY_SUMMARY:
        return esFormFields;
      case enumCompanyPlacement.ISF:
        return isfFormFields;
      case enumCompanyPlacement.SHIPMENT:
        return lclFormFields;
      case enumCompanyPlacement.COMPANY_PAGE:
        return fullCompanyFormFields;
      case enumCompanyPlacement.CBP_ASSIGNED_NUMBER_REQUEST:
        return cbpAssignedNumberRequestFormFields;
      case enumCompanyPlacement.CONTINUOUS_BOND:
        return bondsFormFields;
    }
  }

  private sideBarValidationKeys(
    placement: enumCompanyPlacement,
    isUnitedStates?: boolean,
    isAllowedForCustoms?: boolean,
    isChangesAfterValidationDate?: boolean,
  ): Array<{ key: string; validators: Validators[] }> {
    switch (placement) {
      case enumCompanyPlacement.ENTRY_SUMMARY:
        return esSideBarValidationKeys;
      case enumCompanyPlacement.ISF:
        return isfSideBarValidationKeys;
      case enumCompanyPlacement.SHIPMENT:
        return lclSideBarValidationKeys;
      case enumCompanyPlacement.COMPANY_PAGE:
        if (isChangesAfterValidationDate) {
          return isUnitedStates || (isAllowedForCustoms && !isUnitedStates) ? usCompanyChangesKeys : nonUsCompanyChangesKeys;
        }
        return fullCompanyFormKeys;
      case enumCompanyPlacement.CONTINUOUS_BOND:
        return bondsSideBarValidationKeys;
    }
  }

  private static readonly ORG_COMPANY_PREF_KEY = 'orgCompanyPreference';

  private saveOrgCompanyPreference(organizationId: number, companyId: number): void {
    const prefs = this.getOrgCompanyPreferences();
    prefs[organizationId] = companyId;
    localStorage.setItem(CompaniesService.ORG_COMPANY_PREF_KEY, JSON.stringify(prefs));
  }

  private getOrgCompanyPreferences(): { [orgId: number]: number } {
    try {
      return JSON.parse(localStorage.getItem(CompaniesService.ORG_COMPANY_PREF_KEY) || '{}');
    } catch {
      return {};
    }
  }
}
